// The sandbox screen's playback bar (`client-sandbox-screen`; design **T4**/**T6**; design doc
// **D4**/**D10**/**D12**/**D14**; visual spec §3; sketch `playback-states.html`).
//
// Two rows, and the change's headline control. Row 1 is the transport: one primary button, the
// speed ladder, and the version-selector slot. Row 2 is the scrub track: a three-layer bar with
// season bands, an `mm:ss` clock at each end, and a draggable playhead.
//
// Four rules are load-bearing enough to state here, because each is a thing a well-meaning edit
// would otherwise undo:
//
//   * **The transport's box is 44 × 44 px in every state** (**D4**), and only its colour and symbol
//     differ. The size comes from `TRANSPORT_CONTROL_SIZE_PX`, one constant applied unconditionally,
//     so no state can carry a different geometry — this row is where the player's thumb lives, and
//     a control that resizes as its state changes makes the row twitch under it. Only the version
//     selector flexes.
//   * **There is exactly one transport control and no separate stop control.** Returning to the
//     start is the scrub thumb's job; a second control beside the primary one would be two controls
//     for one intent. `run-submit-model.ts` produces no such state, and nothing here invents one.
//   * **`blocked` is `aria-disabled` with a live click handler, never the DOM `disabled`
//     attribute** (**T6**). The attribute swallows the click, and the click is the one gesture the
//     player will certainly try — it is how they discover *why* Run is refusing. Tapping raises the
//     compiler's own diagnostics (type, line, message, verbatim) plus a control that opens the first
//     offending location. `TransportControlState` therefore hands us `ariaDisabled` and
//     `disabledAttribute` as two separate fields, and this component never collapses them.
//   * **While a SERVER run is in flight the track is determinate progress and nothing more**
//     (architecture review **A1**, **D-182**): that trace publishes atomically, so an unpublished
//     run's bytes are absent by rule and no seek may resolve into the written extent. On publication
//     the whole range becomes seekable.
//
//     **A device run is the other half of that split and is genuinely draggable while it writes**,
//     because this client owns the writer — up to the head of what has actually crossed to the
//     drawing thread, which is `WrittenExtent.watchedHeadTick` and not the run's progress count.
//     The two part company after `DEFAULT_WATCH_WINDOW_TICKS`: the run keeps simulating and keeps
//     reporting progress, but no further world state is handed over.
//
//     Both the layers and the adjudication still come from `written-extent.ts`, so the *gesture* and
//     the *refusal* cannot disagree. One known gap, recorded rather than left to be discovered: the
//     hatched written band is `ticksObserved` — deliberately, it is the run's progress readout — so
//     between the watched head and the write head the band depicts ground a drag will refuse.
//     Correcting that means depicting the two regions separately, which is a visual change and
//     belongs to the `prototype/` kit rather than to this fix.
//
// Season bands come from `SEASON_BANDS` in `SeasonClock.tsx` rather than a private copy of the
// 40/35/25 split, so the track and every other season readout on the screen read one table.
//
// Two deliberate departures from the sketch, both recorded rather than silently taken:
//
//   1. **The speed steps are 44 × 44, not the sketch's 40 × 44.** Visual spec §0.3 lists the 40 px
//      width as a knowing exception to the 44 px minimum; UI.md §2 does not grant one, and the row
//      already has an element specified to absorb the difference — the version selector is the only
//      thing that flexes (**D4**), so widening the ladder by 12 px costs the selector 12 px and
//      moves nothing else.
//   2. **The busy spinner rotates via the Web Animations API**, not a `@keyframes` rule: this
//      component styles inline from tokens and adds no stylesheet. The rotation is decorative — the
//      meaning is carried by `aria-busy`, the label, and the determinate track (**D10**), which is
//      where the design puts run progress — so its absence under `prefers-reduced-motion` or in a
//      renderer without `Element.animate` costs nothing.

import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from 'react';
import { clockLabel, transportClock } from '../../rounds/review-model';
import {
  DEFAULT_SANDBOX_SPEED,
  SANDBOX_SPEED_STEPS,
  TRANSPORT_CONTROL_SIZE_PX,
  sandboxSpeedLabel,
  type SandboxSpeed,
  type TransportActivation,
  type TransportControlState,
  type TransportTone,
} from '../../rounds/run-submit-model';
import {
  decideSeek,
  seekRefusalFor,
  trackLayers,
  type SeekDecision,
  type WrittenExtent,
} from '../../rounds/written-extent';
import { SEASON_BANDS } from './SeasonClock';

/**
 * The clock's placeholder where there is no run (visual spec §3.4): an em dash, a colon, an em
 * dash. Never `00:00`, which is a real position and would read as a run parked at its first tick —
 * exactly the fabricated value the screen's honesty rule forbids.
 */
export const NO_RUN_CLOCK = '—:—';

/**
 * One compiler diagnostic, as the blocked popup shows it. `message` is the compiler's **own** text
 * and is rendered verbatim — a paraphrase would be a second, worse source of truth about a refusal
 * the server will restate anyway.
 *
 * Attribution is nullable because it genuinely can be absent: a document-level error (a dangling
 * birth type, a dropped starting-composition type) belongs to no single slice and carries no span.
 * An unattributed diagnostic renders as its message alone and offers no jump, rather than inventing
 * a location for the player to be sent to.
 */
export interface ScriptDiagnostic {
  /** The bee type whose document slice the error is in, or `null` when it belongs to no slice. */
  readonly typeName: string | null;
  /** 1-based line within that slice, or `null` when the compiler reported no span. */
  readonly line: number | null;
  /** The compiler's own message. Rendered verbatim. */
  readonly message: string;
}

export interface PlaybackBarProps {
  /** The transport control's state, from `transportStateFor`. */
  transport: TransportControlState;
  /** What the screen knows about the selected run's extent — drives both layers and seekability. */
  extent: WrittenExtent;
  /** The playhead, in ticks; `null` where there is no run to be positioned within. */
  playheadTick: number | null;
  tickRateHz: number;
  /** The selected speed step. Omitted means the default, 1×. */
  speed?: SandboxSpeed;
  /**
   * The steps that can apply **right now**, or omitted for all of them.
   *
   * A second, per-step gate beside `speedsOff`, and deliberately not a replacement for it. A run
   * being watched hands this screen only the head of its trace (`DEFAULT_WATCH_WINDOW_TICKS`), and
   * the fast steps eat that in seconds — at 16× the world view empties 3.75 s in and stays empty
   * until the run publishes. So while a run is in flight the caller admits only the steps the window
   * can feed, and the rest render offered-but-inert until publication restores them.
   *
   * The caller decides which steps those are (`admissibleSandboxSpeeds`); this row only draws the
   * answer. Omitted rather than defaulted at every call site, so a bar that knows nothing about
   * watching — every other consumer — is not gated by silence.
   */
  admissibleSpeeds?: readonly SandboxSpeed[];
  onSpeed(step: SandboxSpeed): void;
  /**
   * The primary control was activated. The *intent* is passed rather than the state id, so no
   * caller has to re-derive which of submit / diagnostics / toggle-playback this press meant.
   * `inert` states never call it.
   */
  onActivate(activation: TransportActivation): void;
  /** A drag that resolved to a tick. */
  onSeek(tick: number): void;
  /** A drag that was refused, with the reason to say out loud. */
  onSeekRefused?(decision: Extract<SeekDecision, { kind: 'refused' }>): void;
  /** Whether the track is currently being dragged — `SeekTransport`'s own signal, needed here
   * for the same reason (WAG-36): the world view suppresses crowd state labels for the duration
   * of a scrub, and a resolved tick alone cannot tell a drag from a keyboard step. */
  onDragChange?(dragging: boolean): void;
  /**
   * The version-selector trigger. A slot rather than an import: the selector is its own component
   * with its own menu state, and this bar's job is only to give it the row's one flexing cell and
   * to apply the withheld treatment to it when a turn is in flight.
   */
  versionSelector?: ReactNode;
  /**
   * Re-open the first-run guide (WAG-109). Omitted means no help control is drawn at all —
   * the same absent-means-no-affordance convention `versionSelector` uses, so every bare mount
   * and the graded-review reuse of this bar are untouched.
   *
   * Deliberately **not** gated by `withheld`: the guide explains the screen and is safe to read
   * while a Beekeeper turn holds the mutation surface. It mutates nothing.
   */
  onHelp?(): void;
  /** The working document's diagnostics, shown when a blocked Run is tapped. */
  diagnostics?: readonly ScriptDiagnostic[];
  /** Open the first offending location in the type sheet. */
  onOpenDiagnostic?(diagnostic: ScriptDiagnostic): void;
}

/**
 * The three controls the server refuses for the duration of a Beekeeper turn — Run, Activate and
 * hand edits — withheld together.
 *
 * Exported because the third of them, the hand-edit affordance, lives in the type sheet rather than
 * in this row. A sheet that decided for itself when to withhold would be a second opinion about one
 * server rule, and two surfaces answering the same refusal differently is precisely the failure the
 * rule exists to avoid. One predicate, read by both.
 */
export function mutationSurfaceWithheld(transport: TransportControlState): boolean {
  return transport.id === 'withheld';
}

/**
 * Whether the transport should *look* unavailable (UI/UX audit **F-08**).
 *
 * Deliberately **not** `mutationSurfaceWithheld`, and deliberately not the state `id`. The bug this
 * fixes: `SandboxScreen` composes a phase-gated transport by spreading a registry state and
 * overriding `activation`/`ariaDisabled`/`disabledAttribute`/`label` — but not `id` — so a control
 * that was DOM-`disabled` still rendered at full strength, indistinguishable from a runnable one,
 * while `Send` beside it correctly dimmed. Keying on the identity meant every state composed outside
 * the registry was invisible to the treatment.
 *
 * `disabledAttribute` is the property that actually matters here: it is precisely "this control will
 * not receive the click". That makes it true for `withheld` and for the phase-gated override, and
 * false for `busy` and `blocked` — both of which deliberately keep their click (`busy` shows the
 * player's own run writing and must stay prominent; `blocked` is how the compiler's diagnostics are
 * reached, design **T6**). Two rejected alternatives, recorded so they are not retried:
 *
 *   * `id: 'withheld'` on the override — a hybrid whose `id` disagrees with its own symbol, tone and
 *     label, breaking every consumer that switches on it (including `data-state`).
 *   * `activation === 'inert'` — also true for `busy` (`run-submit-model.ts`), so the spinner would
 *     fade exactly when it must not.
 */
export function transportVisuallyUnavailable(transport: TransportControlState): boolean {
  return transport.disabledAttribute;
}

/** The transport's colour treatment by tone (visual spec §3.1). Colour is the entire signal. */
const TONE_STYLES: Record<TransportTone, CSSProperties> = {
  // "This needs running" — the accent *is* the instruction, and the offset shadow lifts it off the
  // bar so it reads as the row's one live thing.
  accent: {
    // `--accent-fill`, not `--accent` (UI/UX audit F-06): the text here is `--surface-card`, i.e.
    // near-white, which measured 3.16:1 on `--accent`. Note this site was missed by the first sweep
    // because the near-white was expressed as a *token* rather than a literal `#FFFDF6` — worth
    // remembering that "find the light text" cannot be a hex search.
    background: 'var(--accent-fill)',
    borderColor: 'var(--accent-strong)',
    color: 'var(--surface-card)',
    boxShadow: 'var(--shadow-hud)',
  },
  // Reads as unavailable while staying tappable — muted fill, muted ink, ordinary hairline.
  blocked: {
    background: 'var(--paper-2)',
    borderColor: 'var(--line-strong)',
    color: 'var(--ink-3)',
  },
  neutral: {
    background: 'var(--surface-card)',
    borderColor: 'var(--line-strong)',
    color: 'var(--ink-1)',
  },
};

/** Geometry the transport carries in **every** state. Split out so it cannot be conditionalised. */
const TRANSPORT_BOX: CSSProperties = {
  width: TRANSPORT_CONTROL_SIZE_PX,
  height: TRANSPORT_CONTROL_SIZE_PX,
  flex: 'none',
  boxSizing: 'border-box',
  padding: 0,
  borderWidth: 1.5,
  borderStyle: 'solid',
  borderRadius: 'var(--r-md)',
  fontFamily: 'var(--font-display)',
  fontSize: 15,
  lineHeight: 1,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
};

/** The `mm:ss` end-caps. Fixed width because Fredoka ships no tabular figures (visual spec §0.4.3):
 * without it the track's width changes as the clock's digits do, and the thumb drifts. */
const CLOCK_STYLE: CSSProperties = {
  fontFamily: 'var(--font-display)',
  fontWeight: 600,
  fontSize: 11,
  color: 'var(--ink-3)',
  fontVariantNumeric: 'tabular-nums',
  flex: 'none',
  width: 32,
};

/** The run's last tick, or `null` where the screen has no honest source for one. */
function terminalTickOf(extent: WrittenExtent): number | null {
  switch (extent.kind) {
    case 'published':
      return extent.terminalTick;
    case 'in-flight':
      return extent.expectedTerminalTick;
    case 'absent':
      return null;
  }
}

/** A diagnostic as one line: `harvester, line 9 — unknown state: delivr`. */
function diagnosticLine(diagnostic: ScriptDiagnostic): string {
  const where = [
    diagnostic.typeName,
    diagnostic.line === null ? null : `line ${diagnostic.line}`,
  ].filter((part): part is string => part !== null);
  return where.length === 0 ? diagnostic.message : `${where.join(', ')} — ${diagnostic.message}`;
}

export function PlaybackBar({
  transport,
  extent,
  playheadTick,
  tickRateHz,
  speed = DEFAULT_SANDBOX_SPEED,
  admissibleSpeeds,
  onSpeed,
  onActivate,
  onSeek,
  onSeekRefused,
  onDragChange,
  versionSelector,
  onHelp,
  diagnostics,
  onOpenDiagnostic,
}: PlaybackBarProps) {
  const trackRef = useRef<HTMLDivElement | null>(null);
  const draggingRef = useRef(false);
  const spinnerRef = useRef<HTMLSpanElement | null>(null);
  const [diagnosticsOpen, setDiagnosticsOpen] = useState(false);

  const withheld = mutationSurfaceWithheld(transport);
  // F-08: the transport's own dimming follows `disabledAttribute`, not the state identity, so a
  // phase-gated control composed outside the registry dims too. `withheld` still governs the rest of
  // the mutation surface (speeds, version selector), whose rule genuinely is "a turn is in flight".
  const transportDimmed = transportVisuallyUnavailable(transport);
  const layers = trackLayers(extent, playheadTick);
  const refusal = seekRefusalFor(extent);
  const terminalTick = terminalTickOf(extent);
  const hasRun = extent.kind !== 'absent';
  // Nothing to play: no run loaded, nothing runnable, or a turn owns the document (visual spec §3.2
  // applies `.speeds.off` in exactly those states).
  const speedsOff = withheld || transport.activation === 'submit' || transport.activation === 'diagnostics';
  const firstDiagnostic = diagnostics?.[0];

  // The spinner's rotation, without a stylesheet. Absent in jsdom and under reduced-motion, both
  // harmless: the state is announced by `aria-busy` and drawn by the track.
  useEffect(() => {
    const element = spinnerRef.current;
    if (element === null || typeof element.animate !== 'function') {
      return;
    }
    const reduced =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) {
      return;
    }
    const animation = element.animate(
      [{ transform: 'rotate(0deg)' }, { transform: 'rotate(360deg)' }],
      { duration: 900, iterations: Infinity, easing: 'linear' },
    );
    return () => animation.cancel();
  }, [transport.id]);

  // A state that is no longer blocked has no diagnostics to show; leaving the popup up would be a
  // stale value over a document that has moved on.
  useEffect(() => {
    if (transport.activation !== 'diagnostics') {
      setDiagnosticsOpen(false);
    }
  }, [transport.activation]);

  /** The fraction of the track a client-x names, or `null` when the container is unmeasured (the
   * `SeekTransport` unmeasured-container precedent — no layout, no seek, rather than a divide by
   * zero). Out-of-track values are left unclamped; `decideSeek` clamps to the trace's own bounds. */
  function fractionAtClientX(clientX: number): number | null {
    const rect = trackRef.current?.getBoundingClientRect();
    if (rect === undefined || rect.width <= 0) {
      return null;
    }
    return (clientX - rect.left) / rect.width;
  }

  /**
   * Adjudicate a drag. **The refusal is decided before the geometry is read**, deliberately: whether
   * a seek may resolve is a property of the extent alone, so an in-flight run refuses with its
   * reason even where the track has never been measured.
   */
  function resolveSeek(clientX: number): void {
    const fraction = fractionAtClientX(clientX);
    const decision = decideSeek(extent, fraction ?? 0);
    if (decision.kind === 'refused') {
      onSeekRefused?.(decision);
      return;
    }
    if (fraction !== null) {
      onSeek(decision.tick);
    }
  }

  function beginDrag(event: ReactPointerEvent<HTMLDivElement>): void {
    draggingRef.current = true;
    onDragChange?.(true);
    (event.currentTarget as Element & { setPointerCapture?(id: number): void }).setPointerCapture?.(
      event.pointerId,
    );
    resolveSeek(event.clientX);
  }

  function endDrag(event: ReactPointerEvent<HTMLDivElement>): void {
    if (!draggingRef.current) {
      return;
    }
    draggingRef.current = false;
    onDragChange?.(false);
    (
      event.currentTarget as Element & { releasePointerCapture?(id: number): void }
    ).releasePointerCapture?.(event.pointerId);
  }

  function activate(): void {
    if (transport.activation === 'inert') {
      return;
    }
    if (transport.activation === 'diagnostics') {
      setDiagnosticsOpen((open) => !open);
    }
    onActivate(transport.activation);
  }

  const watchedPct = `${layers.watched * 100}%`;

  return (
    <div
      data-testid="playback-bar"
      style={{
        position: 'relative',
        height: 88,
        boxSizing: 'border-box',
        padding: '8px 12px',
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        background: 'var(--hud-bg)',
        borderBottom: '1px solid var(--line)',
      }}
    >
      {/* ── Row 1: transport ───────────────────────────────────────────────────────────── */}
      <div style={{ height: 44, display: 'flex', alignItems: 'center', gap: 8 }}>
        <button
          type="button"
          data-testid="playback-transport"
          data-state={transport.id}
          aria-label={transport.label}
          title={transport.label}
          // Two separate facts, never one. `blocked` sets the first and not the second, so the
          // control looks unavailable and still receives the click that explains it (T6).
          aria-disabled={transport.ariaDisabled || undefined}
          disabled={transport.disabledAttribute}
          aria-busy={transport.busy || undefined}
          onClick={activate}
          style={{
            ...TRANSPORT_BOX,
            ...TONE_STYLES[transport.tone],
            // The spinner needs a slightly sunken field to read against; `busy` is the only state
            // whose fill is not its tone's (visual spec §3.1 row 5).
            ...(transport.busy ? { background: 'var(--paper-1)' } : {}),
            opacity: transportDimmed ? 0.38 : 1,
            cursor: transport.activation === 'inert' ? 'default' : 'pointer',
          }}
        >
          {transport.symbol === null ? (
            <span
              ref={spinnerRef}
              data-testid="playback-transport-spinner"
              aria-hidden
              style={{
                width: 15,
                height: 15,
                boxSizing: 'border-box',
                borderRadius: 8,
                border: '2px solid var(--line-strong)',
                borderTopColor: 'var(--accent)',
              }}
            />
          ) : (
            <span aria-hidden>{transport.symbol}</span>
          )}
          {transport.warningPip ? (
            <span
              data-testid="playback-transport-pip"
              aria-hidden
              style={{
                position: 'absolute',
                top: -3,
                right: -3,
                width: 12,
                height: 12,
                boxSizing: 'border-box',
                borderRadius: 6,
                background: 'var(--danger)',
                border: '2px solid var(--hud-bg)',
              }}
            />
          ) : null}
        </button>

        {/* **The Run label** (UI/UX audit — a bare ▶ over "press Run to simulate this one" copy
            read as a play button, not a start-a-simulation button). A separate pill beside the
            transport rather than a change to it: **D4** fixes the transport's own box at 44 × 44
            in every state, geometry the header calls out as load-bearing, so widening the button
            itself for this one state would be the exact edit that rule exists to forbid. Shown
            only where pressing would genuinely START a run — `submit`, never `diagnostics`, whose
            press raises errors rather than simulating anything — and gone the instant there is a
            trace to play, where the bare ▶ / ⏸ carries the whole meaning again. */}
        {transport.activation === 'submit' ? (
          <span
            data-testid="playback-transport-run-label"
            aria-hidden
            onClick={activate}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: TRANSPORT_CONTROL_SIZE_PX,
              minWidth: 44,
              boxSizing: 'border-box',
              padding: '0 14px',
              marginLeft: -4,
              borderRadius: 'var(--r-pill)',
              ...TONE_STYLES.accent,
              fontFamily: 'var(--font-display)',
              fontWeight: 600,
              fontSize: 15,
              opacity: transportDimmed ? 0.38 : 1,
              cursor: 'pointer',
            }}
          >
            Run
          </span>
        ) : null}

        <div
          data-testid="playback-speeds"
          style={{
            display: 'flex',
            gap: 3,
            flex: 'none',
            opacity: speedsOff ? 0.38 : 1,
          }}
        >
          {SANDBOX_SPEED_STEPS.map((step) => {
            const active = step === speed;
            // **Two gates, composed rather than merged.** `speedsOff` is the whole row going quiet —
            // a Beekeeper turn, or nothing to play — and dims the group. `outrunsWatchWindow` is one
            // step being unsustainable *right now* while the rest stay live, which is a different
            // statement and needs a different treatment: the step keeps its normal weight and gets
            // the reason instead. Collapsing them would hand the ladder back during a turn.
            const outrunsWatchWindow = admissibleSpeeds !== undefined && !admissibleSpeeds.includes(step);
            const unavailable = speedsOff || outrunsWatchWindow;
            return (
              <button
                key={step}
                type="button"
                data-testid={`playback-speed-${step}`}
                aria-pressed={active}
                // A step the whole bar has turned off has nothing to explain, so the plain attribute
                // is right there — unlike the transport, where the refusal itself is the message. A
                // step withheld because the run is still being watched DOES have something to
                // explain: the ladder visibly changes when a run starts, and an unexplained control
                // that stops responding is the reading to avoid.
                disabled={unavailable}
                data-outruns-watch-window={outrunsWatchWindow ? 'true' : undefined}
                title={
                  outrunsWatchWindow
                    ? `${sandboxSpeedLabel(step)} — available once the run finishes`
                    : `${sandboxSpeedLabel(step)} speed`
                }
                onClick={() => onSpeed(step)}
                style={{
                  // 44 × 44, not the sketch's 40 × 44: see the header note. The version selector
                  // absorbs the extra width, which is the one element permitted to flex.
                  width: 'var(--hit-target)',
                  minWidth: 'var(--hit-target)',
                  height: 'var(--hit-target)',
                  boxSizing: 'border-box',
                  borderRadius: 'var(--r-sm)',
                  borderWidth: 1.5,
                  borderStyle: 'solid',
                  borderColor: active ? 'var(--line-strong)' : 'transparent',
                  background: active ? 'var(--paper-1)' : 'transparent',
                  color: active ? 'var(--ink-1)' : 'var(--ink-3)',
                  fontFamily: 'var(--font-display)',
                  fontWeight: 500,
                  fontSize: 13,
                  fontVariantNumeric: 'tabular-nums',
                  cursor: unavailable ? 'default' : 'pointer',
                  // Lighter than the whole-group `speedsOff` dimming above (0.38), and applied to the
                  // step alone: "this one is not available yet" beside live siblings, rather than
                  // "the bar is off". A step that is both simply takes the group's treatment.
                  opacity: outrunsWatchWindow && !speedsOff ? 0.55 : 1,
                }}
              >
                {sandboxSpeedLabel(step)}
              </button>
            );
          })}
        </div>

        {/* The row's one flexing cell. Its content is the screen's to compose. */}
        <div
          data-testid="playback-version-selector"
          data-withheld={withheld ? 'true' : undefined}
          style={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            alignItems: 'center',
            opacity: withheld ? 0.38 : 1,
            pointerEvents: withheld ? 'none' : undefined,
          }}
        >
          {versionSelector}
        </div>

        {/* The help control (WAG-109), last in the row so it reads as chrome rather than as part
            of the transport. It borrows the version trigger's treatment — 1.5px `--line-strong`
            hairline on `--surface-card`, display face, `--ink-1` — because that is the row's
            "quiet, always available" register; the accent belongs to Run alone, and a help
            control competing with it would misstate what the player should press. Circular
            rather than the trigger's `--r-md`, which is what separates "opens an explanation"
            from "opens a menu of versions" at a glance.

            `flex: 'none'` after the one flexing cell, so it pins to the right edge and the
            version selector keeps absorbing the row's slack (**D4**). 44 × 44 like every other
            control here. */}
        {onHelp === undefined ? null : (
          <button
            type="button"
            data-testid="playback-help"
            aria-label="Help — how to play"
            title="How to play"
            onClick={onHelp}
            style={{
              width: TRANSPORT_CONTROL_SIZE_PX,
              height: TRANSPORT_CONTROL_SIZE_PX,
              flex: 'none',
              boxSizing: 'border-box',
              padding: 0,
              borderWidth: 1.5,
              borderStyle: 'solid',
              borderColor: 'var(--line-strong)',
              borderRadius: 'var(--r-pill)',
              background: 'var(--surface-card)',
              // `--accent-strong`, the same brown the version trigger's score numeral uses
              // (`OUTCOME_STYLE.score` in `VersionMenu.tsx`) rather than `--ink-1`. It is the row's
              // established "quiet but not text" ink, so the glyph reads as a control instead of as
              // a stray character, without borrowing Run's `--accent-fill`.
              color: 'var(--accent-strong)',
              fontFamily: 'var(--font-display)',
              fontWeight: 600,
              fontSize: 16,
              lineHeight: 1,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
            }}
          >
            <span aria-hidden>?</span>
          </button>
        )}
      </div>

      {/* ── Row 2: the scrub track ─────────────────────────────────────────────────────── */}
      <div
        data-testid="playback-scrub"
        style={{
          height: 24,
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          opacity: withheld ? 0.4 : 1,
        }}
      >
        <span data-testid="playback-clock-position" style={CLOCK_STYLE}>
          {playheadTick === null || !hasRun ? NO_RUN_CLOCK : clockLabel(playheadTick, tickRateHz)}
        </span>

        <div
          ref={trackRef}
          data-testid="playback-track"
          // The accessible form of the drag affordance, and keyboard parity with it. `aria-disabled`
          // rather than absence: the track is still the run's progress readout while it refuses.
          role="slider"
          tabIndex={refusal === null ? 0 : -1}
          aria-label="Playback position"
          aria-valuemin={0}
          aria-valuemax={terminalTick ?? 0}
          aria-valuenow={playheadTick ?? 0}
          aria-valuetext={
            terminalTick === null || playheadTick === null
              ? NO_RUN_CLOCK
              : transportClock(playheadTick, terminalTick, tickRateHz)
          }
          aria-disabled={refusal !== null || undefined}
          data-determinate={layers.determinate ? 'true' : 'false'}
          onPointerDown={beginDrag}
          onPointerMove={(event) => {
            if (draggingRef.current) {
              resolveSeek(event.clientX);
            }
          }}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          onKeyDown={(event) => {
            if (refusal !== null || terminalTick === null) {
              return;
            }
            const page = Math.max(1, Math.round(terminalTick * 0.05));
            const deltas: Record<string, number> = {
              ArrowLeft: -1,
              ArrowRight: 1,
              ArrowDown: -1,
              ArrowUp: 1,
              PageDown: -page,
              PageUp: page,
              Home: -terminalTick,
              End: terminalTick,
            };
            const delta = deltas[event.key];
            if (delta !== undefined) {
              event.preventDefault();
              onSeek(Math.min(terminalTick, Math.max(0, (playheadTick ?? 0) + delta)));
            }
          }}
          style={{
            position: 'relative',
            flex: 1,
            minWidth: 0,
            height: 8,
            borderRadius: 4,
            background: 'var(--paper-2)',
            cursor: refusal === null ? 'ew-resize' : 'default',
            touchAction: 'none',
          }}
        >
          {/* The 8px track is the *visual*; the pointer surface is thumb-sized. Absolutely
              positioned, so it overhangs the 24px row without changing its geometry — exactly what
              the sketch's dashed `.hit` annotation documents. */}
          <div
            data-testid="playback-track-hit"
            aria-hidden
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              top: '50%',
              height: 'var(--hit-target)',
              transform: 'translateY(-50%)',
            }}
          />

          {/* No run: the track carries no children at all (visual spec §3.4) rather than a 0% bar,
              which reads as a stalled run — the fabricated value the honesty rule forbids. */}
          {hasRun ? (
            <>
              {/* Layer 1 — the whole run's season colours span the whole timeline. */}
              <div
                data-testid="playback-track-bands"
                aria-hidden
                style={{
                  position: 'absolute',
                  inset: 0,
                  borderRadius: 4,
                  overflow: 'hidden',
                  display: 'flex',
                }}
              >
                {SEASON_BANDS.map((band, index) => {
                  const from = index === 0 ? 0 : SEASON_BANDS[index - 1].to;
                  return (
                    <span
                      key={band.name}
                      style={{
                        display: 'block',
                        height: '100%',
                        width: `${(band.to - from) * 100}%`,
                        background: band.color,
                      }}
                    />
                  );
                })}
              </div>

              {/* Layer 2 — not yet written. Retreats as the run writes; omitted where the span is
                  unknown, because a proportion we cannot justify is worse than none. */}
              {layers.unwritten === null ? null : (
                <div
                  data-testid="playback-track-unwritten"
                  aria-hidden
                  style={{
                    position: 'absolute',
                    right: 0,
                    top: 0,
                    bottom: 0,
                    width: `${layers.unwritten * 100}%`,
                    borderRadius: '0 4px 4px 0',
                    background: 'var(--paper-2)',
                    boxShadow: 'inset 1px 0 0 var(--line-strong)',
                  }}
                />
              )}

              {/* Layer 3 — the **hatched written extent**, from the playhead to the write head
                  (design **D-J**, sketch `practice-framing.html` panel B's `.writing`).

                  Hatching rather than a second solid fill, and rendered only while a run is
                  genuinely being written: it is what makes "the playhead is chasing the write
                  head" legible as progress rather than as a stall, and UI.md §4B requires the
                  written extent be visually distinct in **both** execution sites — where a seek
                  is refused it says why the drag did nothing, and where a seek is honoured it says
                  how far the drag may go. A published run has no write head, so it draws none. */}
              {layers.writing === null ? null : (
                <div
                  data-testid="playback-track-writing"
                  aria-hidden
                  style={{
                    position: 'absolute',
                    left: `${layers.writing.from * 100}%`,
                    top: 0,
                    bottom: 0,
                    width: `${(layers.writing.to - layers.writing.from) * 100}%`,
                    backgroundImage:
                      'repeating-linear-gradient(90deg, var(--honey-300) 0 6px, var(--honey-200) 6px 12px)',
                    opacity: 0.85,
                  }}
                />
              )}

              {/* Layer 4 — the ink veil over what has already been watched. */}
              <div
                data-testid="playback-track-watched"
                aria-hidden
                style={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: watchedPct,
                  borderRadius: '4px 0 0 4px',
                  background: 'rgba(43, 33, 20, 0.38)',
                }}
              />

              <div
                data-testid="playback-track-thumb"
                aria-hidden
                style={{
                  position: 'absolute',
                  left: watchedPct,
                  top: '50%',
                  width: 18,
                  height: 18,
                  boxSizing: 'border-box',
                  margin: '-9px 0 0 -9px',
                  borderRadius: 9,
                  background: 'var(--hud-bg)',
                  border: '2px solid var(--hud-line)',
                  boxShadow: 'var(--shadow-hud)',
                  cursor: refusal === null ? 'grab' : 'default',
                }}
              />
            </>
          ) : null}
        </div>

        <span data-testid="playback-clock-duration" style={{ ...CLOCK_STYLE, textAlign: 'right' }}>
          {terminalTick === null ? NO_RUN_CLOCK : clockLabel(terminalTick, tickRateHz)}
        </span>
      </div>

      {/* ── The script-error popup (visual spec §3.5) ──────────────────────────────────── */}
      {diagnosticsOpen && diagnostics !== undefined && diagnostics.length > 0 ? (
        <div
          data-testid="playback-error-popup"
          role="dialog"
          aria-label="Script errors"
          style={{
            position: 'absolute',
            left: 12,
            right: 12,
            bottom: 'calc(100% + 14px)',
            zIndex: 26,
            background: 'var(--hud-bg)',
            border: '1.5px solid var(--danger)',
            borderRadius: 'var(--r-md)',
            boxShadow: '0 -6px 20px rgba(43, 33, 20, 0.22)',
            padding: '11px 13px',
          }}
        >
          <h5
            style={{
              margin: '0 0 7px',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              fontFamily: 'var(--font-display)',
              fontWeight: 600,
              fontSize: 14,
              color: 'var(--danger)',
            }}
          >
            <span aria-hidden>⚠</span> This draft won&rsquo;t compile
          </h5>
          <div
            data-testid="playback-error-diagnostics"
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 11,
              lineHeight: 1.6,
              color: 'var(--ink-2)',
              background: 'var(--danger-soft)',
              borderRadius: 'var(--r-sm)',
              padding: '7px 9px',
              marginBottom: 8,
            }}
          >
            {diagnostics.map((diagnostic, index) => (
              <div key={`${diagnostic.typeName ?? ''}:${diagnostic.line ?? ''}:${index}`}>
                {diagnosticLine(diagnostic)}
              </div>
            ))}
          </div>
          {/* Offered only where the compiler gave a location. An "open" control that cannot say
              where would send the player somewhere invented. */}
          {firstDiagnostic !== undefined && firstDiagnostic.typeName !== null ? (
            <button
              type="button"
              data-testid="playback-error-open"
              onClick={() => onOpenDiagnostic?.(firstDiagnostic)}
              style={{
                width: '100%',
                minHeight: 'var(--hit-target)',
                boxSizing: 'border-box',
                borderRadius: 'var(--r-pill)',
                borderWidth: 1.5,
                borderStyle: 'solid',
                borderColor: 'var(--line-strong)',
                background: 'var(--surface-card)',
                fontFamily: 'var(--font-display)',
                fontWeight: 500,
                fontSize: 13,
                color: 'var(--ink-1)',
                cursor: 'pointer',
              }}
            >
              {firstDiagnostic.line === null
                ? `Open ${firstDiagnostic.typeName} →`
                : `Open ${firstDiagnostic.typeName} at line ${firstDiagnostic.line} →`}
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
